import React from "react";
import { ArrowUpRight, Mail, ExternalLink } from "lucide-react";
import RevealRoot from "@/components/landing/RevealRoot";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import { usePrice } from "@/lib/price";

const WHOP = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

const SECTION_PALETTE = ["bg-[#FFE600]", "bg-white", "bg-[#0055FF] text-white", "bg-[#FF3B30] text-white", "bg-[#00FF66]", "bg-[#FF00FF] text-white", "bg-[#FDFBF7]", "bg-[#0a0a0a] text-[#FDFBF7]"];

export default function LegalPage({ pageTitle, kicker, title, effectiveDate, intro, sections, accent = "#FFE600", seoDescription, slug }) {
  const { price } = usePrice();
  return (
    <div data-testid={`legal-${pageTitle.toLowerCase().replace(/\s+/g, "-")}`} className="bg-[#FDFBF7] text-[#0a0a0a] min-h-screen">
      <RevealRoot>
        <SEO
          title={`${pageTitle} | JobHacki`}
          description={seoDescription}
          canonical={`https://jobhacki.com/${slug || pageTitle.toLowerCase().replace(/\s+/g, "-")}`}
        />
        <Navbar />
        {/* Hero */}
        <section
          data-testid="legal-hero"
          className="relative overflow-hidden border-b-4 border-[#0a0a0a] bg-grid"
          style={{ background: accent }}
        >
          <div className="pointer-events-none absolute -top-16 -right-16 w-[280px] h-[280px] bg-[#0a0a0a] border-4 border-[#0a0a0a] rotate-12 hidden md:block" />
          <div className="relative max-w-[1100px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-14 md:pb-16">
            <div className="reveal">
              <div className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-xs md:text-sm shadow-brutal-sm">
                {kicker || "LEGAL"}
              </div>
              <h1
                className="font-display mt-6 text-[44px] sm:text-6xl md:text-7xl lg:text-[5rem] leading-[0.9]"
                data-testid="legal-title"
              >
                {title}
              </h1>
              <div className="font-mono-body text-sm md:text-base mt-5 text-[#0a0a0a]/75">
                Effective date: <b>{effectiveDate}</b>
              </div>
              {intro && (
                <p className="font-mono-body text-base md:text-lg mt-5 max-w-3xl leading-relaxed text-[#0a0a0a]/85">
                  {intro}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Sections */}
        {sections.map((s, i) => {
          const bg = SECTION_PALETTE[i % SECTION_PALETTE.length];
          return (
            <section
              key={s.title}
              data-testid={`legal-section-${i}`}
              className={`${bg} border-b-4 border-[#0a0a0a]`}
            >
              <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-14 md:py-20">
                <div className="reveal flex items-baseline gap-4 md:gap-6 mb-6">
                  <span className="font-display text-5xl md:text-6xl opacity-30 select-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-display text-2xl md:text-4xl lg:text-5xl leading-[0.95]">
                    {s.title}
                  </h2>
                </div>
                <div className="reveal font-mono-body text-[15px] md:text-base leading-relaxed space-y-4 max-w-3xl">
                  {s.body}
                </div>
              </div>
            </section>
          );
        })}

        {/* Contact band */}
        <section data-testid="legal-contact" className="border-b-4 border-[#0a0a0a] bg-[#FFE600]">
          <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8 reveal">
              <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-3">
                QUESTIONS?
              </div>
              <h3 className="font-display text-3xl md:text-5xl leading-[0.95]">
                Real humans.{" "}
                <span className="bg-[#0a0a0a] text-[#FFE600] px-2">Same inbox.</span>
              </h3>
              <p className="font-mono-body mt-3 text-[#0a0a0a]/80 text-base">
                Send anything legal-, privacy- or refund-related to{" "}
                <a
                  href="mailto:support@jobhacki.com"
                  className="font-bold border-b-2 border-[#0a0a0a]"
                >
                  support@jobhacki.com
                </a>{" "}
                and we&apos;ll get back to you.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end reveal">
              <a
                href="mailto:support@jobhacki.com"
                data-testid="legal-contact-cta"
                className="hover-pop inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base shadow-brutal"
              >
                <Mail size={16} strokeWidth={3} /> Email support
              </a>
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section data-testid="legal-crosslinks" className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]">
          <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { label: "Privacy", href: "/privacy" },
              { label: "Terms", href: "/terms" },
              { label: "Disclaimer", href: "/disclaimer" },
              { label: "Refund policy", href: "/refund" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="hover-pop bg-white border-4 border-[#0a0a0a] shadow-brutal-sm p-4 font-accent text-sm flex items-center justify-between"
              >
                {l.label}
                <ArrowUpRight size={16} strokeWidth={3} />
              </a>
            ))}
          </div>
        </section>

        {/* Get back to the product */}
        <section data-testid="legal-final" className="border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7]">
          <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-14 md:py-20 text-center">
            <h3 className="font-display text-3xl md:text-5xl leading-[0.95]">
              Got the legal bits.{" "}
              <span className="text-[#FFE600]">Back to building.</span>
            </h3>
            <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/"
                data-testid="legal-back-home"
                className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#FDFBF7] px-6 py-4 font-accent text-base shadow-[6px_6px_0_0_#FDFBF7]"
              >
                Back to JobHacki <ArrowUpRight size={16} strokeWidth={3} />
              </a>
              <a
                href={WHOP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="legal-lock-cta"
                className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white border-4 border-[#FDFBF7] px-6 py-4 font-accent text-base shadow-[6px_6px_0_0_#FDFBF7]"
              >
                Lock {price} <ExternalLink size={14} strokeWidth={3} />
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </RevealRoot>
    </div>
  );
}
